import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LenderChip,
  Price,
  type LenderDisplay,
} from '@hero/web-components';
import type { ApplicationMock, OfferMock } from '../mocks';
import {
  formatOfferFeePercentage,
  formatOfferRepaymentDate,
  formatOfferTerm,
} from '../utils/offerDisplay';

export type OfferDetailsDialogProps = {
  /**
   * The offer being viewed. `null` keeps the dialog closed.
   */
  offer: OfferMock | null;
  application: ApplicationMock;
  /** Resolved lender display (logo, name, brand color), if known. */
  partner?: LenderDisplay;
  onClose: () => void;
};

/**
 * Read-only mirror of {@link AcceptOfferDialog} — same layout, same
 * numbers, no accept button. Opened by clicking the accepted offer
 * card after the deal is locked so the carrier can re-inspect the
 * terms they agreed to without having to rummage through the row
 * details. Used after `decision === 'accepted'` and beyond
 * (`funded`, `awaiting_repayment_approval`, `repaid`).
 *
 * Keeping the layout identical to the accept dialog is intentional:
 * the carrier reads the same headline ("you receive today" vs
 * "you'll repay"), the same locked terms, the same notes block,
 * so the "what did I agree to?" lookup feels immediate.
 */
export function OfferDetailsDialog({
  offer,
  application,
  partner,
  onClose,
}: OfferDetailsDialogProps) {
  const { t } = useTranslation('m-financing');

  if (!offer || !offer.terms) return null;

  const currency = application.invoice.currency;
  const repaymentDateLabel = formatOfferRepaymentDate(offer.terms.repaymentDate);
  const repaymentTotal = offer.terms.advancedAmount + offer.terms.feeAmount;

  return (
    <Dialog
      open={offer !== null}
      onOpenChange={open => {
        if (!open) onClose();
      }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t('offer_card.details.modal_title')}
          </DialogTitle>
          <DialogDescription>
            {t('offer_card.details.modal_description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between gap-3">
            {partner ? (
              <LenderChip lender={partner} size="sm" />
            ) : (
              <span className="text-sm font-semibold text-gray-900">
                {offer.lenderName}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-medium text-emerald-800">
                {t('offer_card.accept.you_receive_today')}
              </p>
              <Price
                amount={offer.terms.advancedAmount}
                currency={currency}
                size="lg"
                className="mt-1 font-bold text-emerald-700"
              />
              <p className="mt-2 text-xs leading-relaxed text-emerald-800/80">
                {t('offer_card.accept.receive_hint')}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-700">
                {t('offer_card.accept.you_will_repay')}
              </p>
              <Price
                amount={repaymentTotal}
                currency={currency}
                size="lg"
                className="mt-1 font-bold text-gray-900"
              />
              <p className="mt-2 text-xs leading-relaxed text-gray-600">
                {t('offer_card.details.repay_hint')}
              </p>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-gray-200 bg-white p-3 text-sm">
            <div>
              <dt className="text-xs text-gray-500">
                {t('offer_card.fields.repayment_date')}
              </dt>
              <dd className="mt-0.5 text-sm font-medium text-gray-900">
                {repaymentDateLabel}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">
                {t('offer_card.fields.term')}
              </dt>
              <dd className="mt-0.5 text-sm font-medium text-gray-900">
                {formatOfferTerm(t, offer)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">
                {t('offer_card.fields.fee')}
              </dt>
              <dd className="mt-0.5 font-semibold text-gray-900">
                <Price amount={offer.terms.feeAmount} currency={currency} />
                <span className="ms-1 text-xs font-normal text-gray-500">
                  ({formatOfferFeePercentage(offer.terms.feePercentage)})
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">
                {t('offer_card.fields.advanced_amount')}
              </dt>
              <dd className="mt-0.5 font-semibold text-gray-900">
                <Price amount={offer.terms.advancedAmount} currency={currency} />
              </dd>
            </div>
            {offer.terms.notes && (
              <div className="col-span-2 rounded-lg bg-gray-50 p-2.5">
                <dt className="text-xs text-gray-500">
                  {t('offer_card.fields.notes')}
                </dt>
                <dd className="mt-0.5 text-xs leading-relaxed text-gray-700">
                  {offer.terms.notes}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('offer_card.details.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
